import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch } from "../../state/store";

import { colors, strCode } from "../../consts";
import { t } from "../../services";
import { useMinMaxInput } from "../../hooks";

import { FlexContainers, IconButton, Input, SVG, WrapperContainer } from "../../components";
import { TablePaginationWrapper, PaginationInfoText, PaginationInputWrapper } from "./styled";
import { setTableRowsPerPage } from "../../state/ducks";
import { selectTableRowsPerPage } from "../../state/selectors";

const minValue = 4;
const maxValue = 60;

const TablePagination = ({
  tableSize,
  pageIndex,
  pageSize,
  setPageSize,
  canPreviousPage,
  canNextPage,
  onPrevPage,
  onNextPage,
  onFirstPage,
  onLastPage,
  tableKey
}) => {
  const [recordsFrom, setRecordsFrom] = useState(1);
  const [recordsTo, setRecordsTo] = useState(tableSize);

  const tableRowsPerPage = useSelector(selectTableRowsPerPage(tableKey));

  const {
    result: rowsPerPage,
    value,
    setValue,
    onBlur
  } = useMinMaxInput(tableRowsPerPage, minValue, maxValue);

  useEffect(() => {
    setPageSize(parseInt(rowsPerPage));
    dispatch(setTableRowsPerPage(tableKey, parseInt(rowsPerPage)));
  }, [rowsPerPage]);

  useEffect(() => {
    const start = pageIndex * pageSize + 1;
    const end = (pageIndex + 1) * pageSize;

    setRecordsFrom(start);
    setRecordsTo(end > tableSize ? tableSize : end);
  }, [tableSize, pageSize, pageIndex]);

  return (
    <TablePaginationWrapper
      Container={FlexContainers.FlexRowEnd}
      width="100%"
      mt={16}
      gap={48}
      wrap={"wrap-reverse"}>
      <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8}>
        <PaginationInfoText>{t(strCode.ROWS_PER_PAGE)}</PaginationInfoText>

        <PaginationInputWrapper Container={FlexContainers.FlexRowCenter} width={66}>
          <Input
            id="row-per-page"
            type={"number"}
            value={value}
            setValue={(v) => setValue(v)}
            hasError={value < minValue || value > maxValue}
            onBlur={onBlur}
            maxLength={2}
          />
        </PaginationInputWrapper>
      </WrapperContainer>

      <WrapperContainer>
        <PaginationInfoText>{`${recordsFrom}-${recordsTo} ${t(
          strCode.OF
        )} ${tableSize}`}</PaginationInfoText>
      </WrapperContainer>

      <WrapperContainer Container={FlexContainers.FlexRowCenter} gap={24}>
        <IconButton onClick={onFirstPage} disabled={!canPreviousPage}>
          <SVG.NavigateEndIcon flipped color={colors.RAISIN_80} darkModeColor={colors.RAISIN_20} />
        </IconButton>
        <IconButton onClick={onPrevPage} disabled={!canPreviousPage}>
          <SVG.NavigateNextIcon flipped color={colors.RAISIN_80} darkModeColor={colors.RAISIN_20} />
        </IconButton>
        <IconButton onClick={onNextPage} disabled={!canNextPage}>
          <SVG.NavigateNextIcon color={colors.RAISIN_80} darkModeColor={colors.RAISIN_20} />
        </IconButton>
        <IconButton onClick={onLastPage} disabled={!canNextPage}>
          <SVG.NavigateEndIcon color={colors.RAISIN_80} darkModeColor={colors.RAISIN_20} />
        </IconButton>
      </WrapperContainer>
    </TablePaginationWrapper>
  );
};

TablePagination.propTypes = {
  tableSize: PropTypes.number.isRequired,
  pageIndex: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPageSize: PropTypes.func.isRequired,
  canPreviousPage: PropTypes.bool.isRequired,
  canNextPage: PropTypes.bool.isRequired,
  onPrevPage: PropTypes.func.isRequired,
  onNextPage: PropTypes.func.isRequired,
  onFirstPage: PropTypes.func.isRequired,
  onLastPage: PropTypes.func.isRequired,
  tableKey: PropTypes.string.isRequired
};

export default TablePagination;
